import { z } from 'zod';

import { MoneySchema, TimestampSchema } from './primitives.js';
import {
  AttributionSourceSchema,
  PaymentMethodSchema,
  type AttributionSource,
  type PaymentMethod,
} from './ticket.js';

/**
 * Daily outlet rollup — the O(1) source for the operator `/reports` page.
 *
 * Stored at `tenants/{tid}/outlets/{oid}/stats/{YYYY-MM-DD}` by the nightly
 * `aggregationsDaily` Cloud Function (`apps/functions/src/aggregations/rollover.ts`),
 * which folds one SGT calendar day's `ticketsHistory` (queried by
 * `payment.receivedAt`) into a single doc. Reports then reads ≤ ~34 daily docs
 * for a range instead of scanning the whole ticket archive (audit 2026-07-05
 * §2 P1, cost hotspot #2).
 *
 * The doc id IS the day key so the job upserts idempotently and Reports can
 * range-query by document id / the mirrored `day` field.
 *
 * Days with zero paid tickets are intentionally NOT written (no wasted write /
 * read for idle outlets) — a missing doc reads as a zero day.
 */

/** One payment-method slice of a day's revenue. */
export const DailyStatsMethodBucketSchema = z.object({
  method: PaymentMethodSchema.describe('Payment method this slice aggregates'),
  count: z.number().int().nonnegative().describe('Paid tickets settled with this method'),
  revenueSen: MoneySchema.describe('Gross revenue for this method, integer sen'),
});
export type DailyStatsMethodBucket = z.infer<typeof DailyStatsMethodBucketSchema>;

/** One package slice of a day's revenue. */
export const DailyStatsPackageBucketSchema = z.object({
  packageId: z.string().describe("Snapshot packageId, or 'no-package' when the ticket had none"),
  packageName: z.string().describe('Human-readable package name at time of wash'),
  count: z.number().int().nonnegative().describe('Paid tickets for this package'),
  revenueSen: MoneySchema.describe('Gross revenue for this package, integer sen'),
});
export type DailyStatsPackageBucket = z.infer<typeof DailyStatsPackageBucketSchema>;

/**
 * One attribution slice of a day: how many paid washes arrived through a link
 * shared on this channel (Share & grow). Tickets with no attribution are NOT
 * counted here — "came in off the street" is not a source anyone shared.
 */
export const DailyStatsSourceBucketSchema = z.object({
  source: AttributionSourceSchema.describe('Channel the booking link was shared on'),
  count: z.number().int().nonnegative().describe('Paid tickets that arrived via that channel'),
});
export type DailyStatsSourceBucket = z.infer<typeof DailyStatsSourceBucketSchema>;

/**
 * The pure reduction of a day's tickets — the numbers, without the doc
 * identity/timestamp envelope. `rollupTicketsToStats` returns this; both the
 * stored doc and the live "today" rollup in `/reports` share this shape so they
 * merge cleanly.
 */
export const DailyStatsRollupSchema = z.object({
  ticketCount: z.number().int().nonnegative().describe('Paid tickets rolled into this day'),
  grossRevenueSen: MoneySchema.describe('Sum of payment.amount across the day, integer sen'),
  byMethod: z
    .array(DailyStatsMethodBucketSchema)
    .describe('Per-payment-method breakdown, sorted by revenue desc'),
  byPackage: z
    .array(DailyStatsPackageBucketSchema)
    .describe('Per-package breakdown, sorted by revenue desc'),
  bySource: z
    .array(DailyStatsSourceBucketSchema)
    .default([])
    .describe(
      'Paid tickets per attribution source, sorted by count desc. Defaults to [] so every doc written before Share & grow still parses.',
    ),
});
export type DailyStatsRollup = z.infer<typeof DailyStatsRollupSchema>;

/** The persisted daily-stats doc. */
export const DailyStatsSchema = DailyStatsRollupSchema.extend({
  id: z.string().describe('Doc id — the SGT calendar day, YYYY-MM-DD'),
  day: z.string().describe('SGT calendar day, YYYY-MM-DD (mirrors id for range queries)'),
  tenantId: z.string().describe('Owning tenant id'),
  outletId: z.string().describe('Owning outlet id'),
  computedAt: TimestampSchema.describe('When the rollover job last wrote this doc'),
});
export type DailyStats = z.infer<typeof DailyStatsSchema>;

/**
 * Minimal structural view of a ticket the rollup needs. `Ticket` (from
 * `ticket.ts`) is a supertype, so a `Ticket[]` passes without a cast — but the
 * loose shape also lets the nightly job pass raw Firestore data and the test
 * pass plain literals.
 */
export interface RollupTicketInput {
  readonly payment?: { readonly method?: PaymentMethod; readonly amount?: number } | undefined;
  readonly snapshot?: { readonly packageId?: string; readonly packageName?: string } | undefined;
  /** Share & grow attribution. An unknown source is ignored, never counted as "other". */
  readonly attribution?: { readonly source?: unknown } | undefined;
}

const NO_PACKAGE_ID = 'no-package';
const NO_PACKAGE_NAME = '(no package)';

/**
 * Pure ticket → daily-stats reduction. Given the tickets that settled in a
 * single day (the caller has already scoped them by `payment.receivedAt`),
 * produce the counts, gross revenue, and per-method / per-package breakdowns.
 *
 * Mirrors exactly what `/reports` computed in-memory before the rollup existed:
 *   - every ticket counts toward `ticketCount`
 *   - `grossRevenueSen` sums `payment.amount ?? 0`
 *   - `byMethod` only includes tickets that carry a `payment.method`
 *   - `byPackage` includes every ticket, falling back to a `no-package` bucket
 * Both breakdowns come out sorted by revenue descending, ties broken by count.
 */
export function rollupTicketsToStats(tickets: readonly RollupTicketInput[]): DailyStatsRollup {
  let grossRevenueSen = 0;
  const methodMap = new Map<PaymentMethod, DailyStatsMethodBucket>();
  const packageMap = new Map<string, DailyStatsPackageBucket>();
  const sourceMap = new Map<AttributionSource, number>();

  for (const t of tickets) {
    const source = AttributionSourceSchema.safeParse(t.attribution?.source);
    if (source.success) sourceMap.set(source.data, (sourceMap.get(source.data) ?? 0) + 1);

    const amount = t.payment?.amount ?? 0;
    grossRevenueSen += amount;

    const method = t.payment?.method;
    if (method) {
      const existing = methodMap.get(method);
      if (existing) {
        methodMap.set(method, {
          method,
          count: existing.count + 1,
          revenueSen: existing.revenueSen + amount,
        });
      } else {
        methodMap.set(method, { method, count: 1, revenueSen: amount });
      }
    }

    const packageId = t.snapshot?.packageId ?? NO_PACKAGE_ID;
    const packageName = t.snapshot?.packageName ?? NO_PACKAGE_NAME;
    const existingPkg = packageMap.get(packageId);
    if (existingPkg) {
      packageMap.set(packageId, {
        ...existingPkg,
        count: existingPkg.count + 1,
        revenueSen: existingPkg.revenueSen + amount,
      });
    } else {
      packageMap.set(packageId, { packageId, packageName, count: 1, revenueSen: amount });
    }
  }

  const byRevenue = <T extends { readonly revenueSen: number; readonly count: number }>(
    a: T,
    b: T,
  ): number => b.revenueSen - a.revenueSen || b.count - a.count;

  return {
    ticketCount: tickets.length,
    grossRevenueSen,
    byMethod: Array.from(methodMap.values()).sort(byRevenue),
    byPackage: Array.from(packageMap.values()).sort(byRevenue),
    bySource: Array.from(sourceMap, ([source, count]) => ({ source, count })).sort(
      (a, b) => b.count - a.count || a.source.localeCompare(b.source),
    ),
  };
}
